import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Smile, Bold, Italic, Calendar as CalendarIcon, ArrowLeft } from "lucide-react";
import { recomputeAndSaveDay, finalizePastDays } from "../lib/mood";

const DIARY_POSTS_KEY = "rl_diary_posts_v1";

function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function loadPostsAll() {
  try {
    const raw = localStorage.getItem(DIARY_POSTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePostsAll(obj) {
  try { localStorage.setItem(DIARY_POSTS_KEY, JSON.stringify(obj)); } catch {}
}

function uid() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }

export default function DiaryNew() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const initDate = (() => {
    const q = search.get("date");
    if (q) {
      const [y,m,d] = q.split("-").map(Number);
      if (y && m && d) return new Date(y, m-1, d);
    }
    return new Date();
  })();
  const [date, setDate] = useState(initDate);
  const [title, setTitle] = useState("");
  const [type, setType] = useState("journaling");
  const [imageUrl, setImageUrl] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState(5);
  const [open, setOpen] = useState(false);

  const applyWrap = (wrapStart, wrapEnd = wrapStart) => {
    const ta = document.getElementById("diary-content");
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const before = content.slice(0, start);
    const sel = content.slice(start, end);
    const after = content.slice(end);
    const next = before + wrapStart + (sel || "text") + wrapEnd + after;
    setContent(next);
    setTimeout(() => { try { ta.focus(); ta.setSelectionRange(before.length + wrapStart.length, before.length + wrapStart.length + (sel || "text").length); } catch {} }, 0);
  };

  const insertEmoji = (emoji) => setContent((t) => t + emoji);

  const onSave = () => {
    const all = loadPostsAll();
    const dk = toDateKey(date);
    const arr = all[dk] || [];
    arr.push({ id: uid(), ts: Date.now(), title: title || "Journal", type, imageUrl, content, mood: Number(mood) });
    all[dk] = arr;
    savePostsAll(all);
    // Recompute mood for that day (if not locked) and ensure past days are finalized
    try {
      recomputeAndSaveDay(dk);
      finalizePastDays();
    } catch {}
    try { window.dispatchEvent(new Event("rl-diary-changed")); } catch {}
    nav(`/diary/day?date=${encodeURIComponent(dk)}`);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <div className="mb-3 sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => nav(`/diary/day?date=${encodeURIComponent(toDateKey(date))}`)} data-testid="diary-new-back-button"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div className="text-xl font-semibold">New journal post</div>
        </div>
        <div className="flex items-center gap-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Change date">
                <CalendarIcon className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0">
              <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); setOpen(false); } }} initialFocus />
            </PopoverContent>
          </Popover>
          <Button className="btn-primary" onClick={onSave} data-testid="diary-new-save-button">Save</Button>
        </div>
      </div>

      <div className="grid gap-4">
        <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Title" />
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-500">Post type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="journaling">Journaling</SelectItem>
                <SelectItem value="trigger">Trigger</SelectItem>
                <SelectItem value="relapse">Relapse</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Image URL (optional)</label>
            <Input value={imageUrl} onChange={(e)=>setImageUrl(e.target.value)} placeholder="https://..." className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Mood (1–10)</label>
            <div className="mt-2 flex items-center gap-3">
              <input type="range" min={1} max={10} value={mood} onChange={(e)=>setMood(parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none cursor-pointer" style={{ background: "linear-gradient(90deg, #FF0000 0%, #FFD93D 50%, #00C853 100%)" }} />
              <span className="text-sm font-semibold" style={{ color: mood <= 5 ? `rgb(${255 - (mood-1)*0}, ${0 + (mood-1)*54}, ${0 + (mood-1)*15})` : `rgb(${255 - (mood-5)*51}, ${217 + (mood-5)*(-17)}, ${61 + (mood-5)*44})`}}>{mood}</span>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button type="button" variant="outline" size="sm" aria-label="Bold" onClick={()=>applyWrap("**","**")}><Bold className="h-4 w-4" /></Button>
            <Button type="button" variant="outline" size="sm" aria-label="Italic" onClick={()=>applyWrap("*","*")}><Italic className="h-4 w-4" /></Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" size="sm" aria-label="Emoji"><Smile className="h-4 w-4" /></Button>
              </PopoverTrigger>
              <PopoverContent className="grid grid-cols-8 gap-1 p-2 w-[220px]">
                {"😀😁😂🤣😊😍🤔😇😎😭😡😴🙏👍👏🔥❤️💪🌟✨🎉".split("").filter(Boolean).map((e,i)=> (
                  <button key={i} className="text-xl" onClick={()=>insertEmoji(e)}>{e}</button>
                ))}
              </PopoverContent>
            </Popover>
          </div>
          <Textarea id="diary-content" value={content} onChange={(e)=>setContent(e.target.value)} placeholder="Write your post... Use **bold**, *italic*, new lines for paragraphs." className="min-h-[260px] md:min-h-[260px]" />
        </div>
      </div>
    </div>
  );
}